import { ApiError } from '../../api/client';
import { uploadErrorMessageKey } from '../../api/uploadErrors';

function sedrErrorCode(cause: unknown): string {
  if (cause instanceof ApiError) {
    return cause.code;
  }
  if (cause instanceof Error) {
    return cause.message;
  }
  return '';
}

/**
 * Maps vault / room-key crypto failures to i18n keys. Returns null when the
 * cause is not a recognized crypto error.
 */
export function sedrCryptoErrorMessageKey(cause: unknown): string | null {
  if (
    (typeof DOMException !== 'undefined' && cause instanceof DOMException && cause.name === 'OperationError')
    || (cause instanceof Error && cause.name === 'OperationError')
  ) {
    return 'sedr.error.roomKeyUnavailable';
  }

  switch (sedrErrorCode(cause)) {
    case 'vault_locked':
      return 'sedr.error.vaultLocked';
    case 'unsupported_wrapped_key_version':
    case 'sedr_room_key_unwrap_failed':
    case 'room_key_missing':
    case 'room_missing':
    case 'invalid_key_length':
      return 'sedr.error.roomKeyUnavailable';
    default:
      return null;
  }
}

/**
 * Maps evidence upload failures (crypto, size limits, SEDR API) to i18n keys.
 */
export function sedrEvidenceUploadErrorMessageKey(cause: unknown): string {
  const cryptoKey = sedrCryptoErrorMessageKey(cause);
  if (cryptoKey) {
    return cryptoKey;
  }

  const uploadKey = uploadErrorMessageKey(cause, 'files.upload.failed');
  if (uploadKey !== 'files.upload.failed') {
    return uploadKey;
  }

  if (cause instanceof ApiError) {
    const sedrKey = sedrErrorMessageKey(cause);
    if (sedrKey !== 'status.error') {
      return sedrKey;
    }
  }

  return 'files.upload.failed';
}

/**
 * Maps evidence open/download failures to i18n keys.
 */
export function sedrEvidenceOpenErrorMessageKey(cause: unknown): string {
  const cryptoKey = sedrCryptoErrorMessageKey(cause);
  if (cryptoKey) {
    return cryptoKey;
  }

  if (cause instanceof ApiError) {
    const sedrKey = sedrErrorMessageKey(cause);
    if (sedrKey !== 'status.error') {
      return sedrKey;
    }
  }

  return 'status.error';
}

/**
 * Maps SEDR API error codes to i18n keys (same pattern as the guest gate).
 */
export function sedrErrorMessageKey(cause: unknown): string {
  const code = sedrErrorCode(cause);

  switch (code) {
    case 'requirement_code_exists':
      return 'sedr.error.requirementCodeExists';
    case 'requirement_fields_required':
      return 'sedr.error.requirementFieldsRequired';
    case 'invalid_status':
    case 'invalid_status_transition':
    case 'invalid_lifecycle_status':
    case 'requirement_lifecycle_closed':
      return 'sedr.error.invalidStatus';
    case 'denial_reason_required':
      return 'sedr.error.denialReasonRequired';
    case 'invalid_category':
      return 'sedr.error.invalidCategory';
    case 'bulk_patch_failed':
    case 'bulk_patch_fields_required':
    case 'bulk_lifecycle_not_supported':
    case 'requirement_ids_required':
    case 'duplicate_requirement_ids':
      return 'sedr.error.bulkPatchFailed';
    case 'superseded_by_required':
    case 'superseded_by_self':
    case 'superseded_by_not_found':
    case 'superseded_by_not_active':
      return 'sedr.error.supersededTargetInvalid';
    case 'lifecycle_apply_failed':
    case 'lifecycle_reject_failed':
      return 'sedr.error.lifecycleRequestFailed';
    case 'invalid_approval_validity':
    case 'invalid_approval_validity_unit':
    case 'invalid_approval_validity_days':
      return 'sedr.error.invalidApprovalValidity';
    case 'requirement_immutable':
      return 'sedr.error.requirementImmutable';
    case 'evidence_outside_grace':
      return 'sedr.error.evidenceOutsideGrace';
    case 'invalid_grace_days':
      return 'sedr.error.invalidGraceDays';
    case 'grace_requires_validity':
      return 'sedr.error.graceRequiresValidity';
    case 'requirement_not_found':
      return 'sedr.error.requirementNotFound';
    case 'admin_quorum_required':
      return 'sedr.access.adminQuorum';
    case 'member_exists':
      return 'sedr.error.memberExists';
    case 'invitee_not_eligible':
      return 'sedr.error.inviteeNotEligible';
    case 'room_setup_incomplete':
      return 'sedr.error.roomSetupIncomplete';
    case 'room_not_setup_incomplete':
      return 'sedr.error.roomNotSetupIncomplete';
    case 'guest_identity_required':
      return 'sedr.error.guestIdentityRequired';
    case 'expires_at_must_be_future':
      return 'sedr.error.expiresAtMustBeFuture';
    case 'deletion_already_requested':
      return 'sedr.error.deletionAlreadyRequested';
    case 'deletion_reason_required':
      return 'sedr.error.deletionReasonRequired';
    case 'deletion_reason_too_long':
      return 'sedr.error.deletionReasonTooLong';
    case 'deletion_not_requested':
      return 'sedr.error.deletionNotRequested';
    case 'deletion_not_pending':
      return 'sedr.error.deletionNotPending';
    case 'cannot_decide_own_deletion':
      return 'sedr.error.cannotDecideOwnDeletion';
    case 'only_requester_can_withdraw':
      return 'sedr.error.onlyRequesterCanWithdraw';
    case 'room_pending_deletion':
      return 'sedr.error.roomPendingDeletion';
    case 'room_archived':
      return 'sedr.error.roomArchived';
    case 'invalid_room_metadata':
      return 'sedr.error.invalidRoomMetadata';
    case 'second_admin_must_differ':
      return 'sedr.error.secondAdminMustDiffer';
    case 'access_denied':
      return 'sedr.error.accessDenied';
    case 'room_not_found':
      return 'sedr.error.roomNotFound';
    case 'invalid_room_type':
      return 'sedr.error.invalidRoomType';
    case 'email_is_staff_domain':
      return 'sedr.guestInvite.staffDomain';
    case 'email_is_staff_user':
      return 'sedr.guestInvite.staffUser';
    case 'email_already_set':
      return 'sedr.access.emailAlreadySet';
    case 'email_set_not_allowed':
      return 'sedr.access.emailSetNotAllowed';
    case 'email_required':
      return 'sedr.error.emailRequired';
    case 'email_required_for_upgrade':
      return 'sedr.guest.upgradeEmailRequired';
    case 'guest_links_disabled':
      return 'sedr.error.guestLinksDisabled';
    case 'guest_links_required_for_external_audit':
      return 'sedr.error.guestLinksRequiredExternal';
    case 'approval_not_allowed':
      return 'sedr.error.approvalNotAllowed';
    case 'invalid_guest_role':
      return 'sedr.error.invalidGuestRole';
    case 'commenting_not_allowed':
      return 'sedr.error.commentingNotAllowed';
    case 'guest_upload_not_allowed':
      return 'sedr.error.guestUploadNotAllowed';
    case 'link_type_name_required':
      return 'sedr.error.linkTypeNameRequired';
    case 'link_type_not_found':
      return 'sedr.error.linkTypeNotFound';
    case 'link_fields_required':
      return 'sedr.error.linkFieldsRequired';
    case 'link_self_not_allowed':
      return 'sedr.error.linkSelfNotAllowed';
    case 'link_target_invalid':
      return 'sedr.error.linkTargetInvalid';
    case 'invalid_link_type':
      return 'sedr.error.invalidLinkType';
    case 'link_requires_unapproved':
      return 'sedr.error.linkRequiresUnapproved';
    case 'link_already_exists':
      return 'sedr.error.linkAlreadyExists';
    case 'link_not_found':
      return 'sedr.error.linkNotFound';
    case 'starter_set_not_found':
      return 'sedr.error.starterSetNotFound';
    case 'starter_set_forbidden':
      return 'sedr.error.starterSetForbidden';
    case 'starter_set_type_mismatch':
    case 'invalid_starter_set_type':
      return 'sedr.error.starterSetTypeMismatch';
    case 'invalid_folder_name':
    case 'folder_name_conflict':
      return 'sedr.error.folderNameConflict';
    case 'invalid_parent_folder':
    case 'folder_cycle':
      return 'sedr.error.invalidParentFolder';
    case 'starter_set_folder_not_found':
      return 'sedr.error.starterSetFolderNotFound';
    default:
      return 'status.error';
  }
}
